import { productEvent } from "@shared/analytics/productEvents";
import { linkVariants } from "@shared/components/ui/link";
import { JACHIN_REPORTING_ENABLED } from "@solomon-apps/config";
import { formatCurrency } from "@solomon-apps/shared";
import { useSessionStore } from "@solomon-apps/shared";
import { DataTable } from "@solomon-labs/design/components/data";
import type { DataTableColumn } from "@solomon-labs/design/components/data";
import {
    ArrowShortLeftIcon,
    ArrowShortUpRightIcon
} from "@solomon-labs/design/components/icons";
import { SectionHeading } from "@solomon-labs/design/components/layout";
import { MetricCard } from "@solomon-labs/design/components/metrics";
import { MetricValue } from "@solomon-labs/design/components/metrics";
import {
    ButtonIcon,
    Copy,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React from "react";
import { useNavigate } from "react-router-dom";

import { useDistributionsData, useYieldDistributions } from "@/hooks/stats";
import type { ProcessedDistribution } from "@/hooks/stats/useYieldDistributions";

import { DistributionHistory } from "./components/DistributionHistory";

const COLORS = {
    paper: "rgb(var(--color-surface-ground))",
    charcoal: "rgb(var(--color-foreground-base))",
    muted: "rgb(var(--color-foreground-neutral))"
} as const;

function getUsdvDecimals(
    tokens: Array<{ symbol: string; decimals?: number }>
): number {
    const usdv = tokens.find((t) => t.symbol === "USDv");
    return usdv?.decimals ?? 6;
}

/**
 * A column header whose wording alone cannot say which horizon the figure
 * covers. Both APY columns are annualized rates over different windows, so the
 * header carries the definition rather than leaving the reader to guess.
 */
function ExplainedHeader({
    label,
    explanation
}: {
    label: string;
    explanation: string;
}): React.ReactElement {
    return (
        <TooltipProvider delay={300}>
            <Tooltip>
                <TooltipTrigger
                    render={<span className="cursor-help">{label}</span>}
                />
                <TooltipContent side="top">{explanation}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

const COLUMNS: DataTableColumn<ProcessedDistribution>[] = [
    {
        header: "Date",
        cell: (row) => (
            <Text
                variant="small"
                render={
                    <a
                        href={`https://solscan.io/tx/${row.sigFull}`}
                        onClick={() =>
                            productEvent("stats:link_clicked", {
                                entry_point: "stats_distributions",
                                link_type: "transaction_explorer"
                            })
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkVariants()}
                    />
                }
            >
                {row.dateStr}
            </Text>
        ),
        align: "left",
        numeric: false
    },
    {
        header: "Transaction",
        // The displayed signature is truncated, so following the link is the
        // only way to get the full string off this page. `Copy` puts the
        // untruncated signature on the clipboard without widening the column.
        cell: (row) => (
            <Copy
                content={row.sigFull}
                copyLabel="Copy signature"
                copiedLabel="Signature copied"
            >
                <Text
                    variant="small"
                    render={
                        <a
                            href={`https://solscan.io/tx/${row.sigFull}`}
                            onClick={() =>
                                productEvent("stats:link_clicked", {
                                    entry_point: "stats_distributions",
                                    link_type: "transaction_explorer"
                                })
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${linkVariants()} inline-flex items-center gap-1`}
                        />
                    }
                >
                    {row.sigShort}
                    <ArrowShortUpRightIcon className="size-4" />
                </Text>
            </Copy>
        ),
        align: "left",
        numeric: false
    },
    {
        header: (
            <ExplainedHeader
                label="7 Day APY"
                explanation="Annualized earnings by stakers over the seven days before this distribution"
            />
        ),
        cell: (row) => row.apyStr,
        align: "right"
    },
    {
        header: (
            <ExplainedHeader
                label="All Time APY"
                explanation="Annualized earnings by stakers since launch till distribution"
            />
        ),
        cell: (row) => row.allTimeApyStr,
        align: "right"
    },
    {
        header: "Staked",
        cell: (row) => row.stakedStr,
        align: "right"
    },
    {
        header: "Amount",
        cell: (row) => row.amountStr,
        align: "right"
    }
];

function LegacyDistributionsPageV3(): React.ReactElement {
    const navigate = useNavigate();
    const tokens = useSessionStore((s) => s.tokens);
    const usdvDecimals = getUsdvDecimals(tokens);

    const { data, isLoading, isError } = useYieldDistributions(usdvDecimals);
    const { distributions } = useDistributionsData();

    const items = React.useMemo(() => data?.items ?? [], [data?.items]);

    const totalDistributed = React.useMemo(
        () =>
            distributions?.total
                ? formatCurrency(String(distributions.total))
                : "—",
        [distributions]
    );

    const lastDistributionDate = React.useMemo(() => {
        if (!items.length) return "—";
        return new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric"
        }).format(new Date(items[0].timestamp));
    }, [items]);

    return (
        <main className="flex-1 flex flex-col items-center px-4 py-6 md:px-6 md:py-10">
            <div className="w-full max-w-5xl flex flex-col">
                {/* Back nav + page title */}
                <div className="flex items-center gap-2 mb-6 md:mb-8">
                    <ButtonIcon
                        variant="transparent"
                        size="lg"
                        onClick={() => {
                            productEvent("stats:link_clicked", {
                                entry_point: "stats_distributions",
                                target: "rewards"
                            });
                            navigate("/stats");
                        }}
                        aria-label="Back to Stats"
                        icon={<ArrowShortLeftIcon />}
                    />
                    <Text variant="h1" className="text-foreground-base">
                        Distributions
                    </Text>
                </div>

                {/* Summary stats — bracketed by horizontal dividers,
                    columns sit on the page surface with no vertical
                    dividers. Mobile collapses to a single column with
                    horizontal dividers between rows. */}
                <div className="flex flex-col sm:flex-row border-y border-line-base mb-8 md:mb-10">
                    <div className="flex-1 py-4 sm:py-5 border-b sm:border-b-0 border-line-base">
                        <MetricValue
                            label="Total Distributed"
                            value={totalDistributed}
                            valueVariant="number-lg"
                            size="lg"
                            labelClassName="text-foreground-muted"
                            loading={isLoading}
                        />
                    </div>
                    <div className="flex-1 py-4 sm:py-5 border-b sm:border-b-0 border-line-base sm:flex sm:justify-center">
                        <MetricValue
                            label="Total Distributions"
                            value={String(items.length)}
                            valueVariant="number-lg"
                            size="lg"
                            labelClassName="text-foreground-muted"
                            loading={isLoading}
                        />
                    </div>
                    <div className="flex-1 py-4 sm:py-5">
                        <MetricValue
                            label="Last Distribution"
                            value={lastDistributionDate}
                            valueVariant="number-lg"
                            size="lg"
                            className="sm:items-end sm:text-right"
                            labelClassName="text-foreground-muted"
                            loading={isLoading}
                        />
                    </div>
                </div>

                {/* Distribution history table */}
                <SectionHeading>Distribution History</SectionHeading>
                <div className="pt-5">
                    {isError ? (
                        <MetricCard className="p-5" surface="paper-faint">
                            <Text
                                variant="body"
                                style={{ color: COLORS.muted }}
                            >
                                Failed to load distributions.
                            </Text>
                        </MetricCard>
                    ) : !isLoading && items.length === 0 ? (
                        <MetricCard className="p-5" surface="paper-faint">
                            <Text
                                variant="body"
                                style={{ color: COLORS.muted }}
                            >
                                No distributions found.
                            </Text>
                        </MetricCard>
                    ) : (
                        <MetricCard surface="paper-faint">
                            <div className="p-4 md:p-5 overflow-x-auto">
                                {/* why: six columns, two of them full timestamps
                                    or signatures, need more room than a
                                    Tailwind width step provides before the
                                    horizontal scroller takes over. */}
                                <div className="min-w-[720px]">
                                    <DataTable
                                        columns={COLUMNS}
                                        rows={items}
                                        rowKey={(row) => row.id}
                                        loading={isLoading}
                                        skeletonRows={20}
                                        staggerKey="distributions"
                                    />
                                </div>
                            </div>
                        </MetricCard>
                    )}
                </div>
            </div>
        </main>
    );
}

export function DistributionsPageV3(): React.ReactElement {
    const navigate = useNavigate();
    if (!JACHIN_REPORTING_ENABLED) return <LegacyDistributionsPageV3 />;
    return (
        <main className="flex-1 flex flex-col items-center px-4 py-6 md:px-6 md:py-10">
            <div className="w-full max-w-5xl flex flex-col gap-6">
                <ButtonIcon
                    variant="transparent"
                    size="md"
                    icon={<ArrowShortLeftIcon />}
                    aria-label="Back to Stats"
                    onClick={() => {
                        productEvent("stats:link_clicked", {
                            entry_point: "stats_distributions",
                            target: "rewards"
                        });
                        navigate("/stats/rewards");
                    }}
                />
                <DistributionHistory />
            </div>
        </main>
    );
}
